import { useState } from 'react';
import styles from './styles.module.scss';
import Typography from '@components/common/typography';
import Input from '@components/common/input';
import FormField from '@components/form_field';
import DatePicker from '@components/date_picker';
import { formatDate, getBirthDisabledDates, mapOccupationsToOptions } from '@utils/index';
import { useLocales } from '@hooks/helpers/useLocales';
import { useFormContext } from 'react-hook-form';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { apiUserService } from '@api';
import { useUserContext } from '@hooks/context/useUserContext';
import Select from '@components/common/select';
import { EUserStatus, occupation } from '@xeppt/xeppt-sdk/types';
import Button from '@components/common/button';
import { Icon } from '@components/icons';

import useResponsive from '@hooks/helpers/useResponsive';

const PersonalSection = () => {
    // const { t } = useTranslation('sections', {
    //     keyPrefix: 'settings.contact'
    // });
    const { labelLocale, submitLocale, requestErrorLocale, requestSuccessLocale } = useLocales();
    const form = useFormContext();
    const [isEdit, setIsEdit] = useState(false);
    const { refetchUser, user } = useUserContext();
    const { isMobile } = useResponsive();

    const { handleRequest: handleUpdateUser, isLoading } = useApiMutation({
        method: () => {
            const {
                profile: { first_name, last_name, birth_date, occupancy }
            } = form.getValues();
            return apiUserService.updateProfile({
                first_name,
                last_name,
                birth_date,
                occupancy
            });
        },
        onSuccess: () => {
            requestSuccessLocale('update_profile');
            refetchUser();
            setIsEdit(false);
        },
        onError: requestErrorLocale
    });

    const onSubmit = () => {
        if (!isEdit) {
            setIsEdit(true);
        } else {
            form.trigger().then((isValid) => {
                isValid && handleUpdateUser(undefined);
            });
        }
    };

    return (
        <>
            <div className={styles.container}>
                <div className={styles.header}>
                    <div className={styles.left}>
                        <div className={styles.icon}>
                            <Icon name="person" />
                        </div>
                        <div className={styles.text_wrapper}>
                            <Typography
                                className={styles.title}
                                variant="body1"
                                lh="120%"
                                weight="semibold">
                                Personal information
                            </Typography>
                            <p className={styles.description}>
                                {user?.status === EUserStatus.UNVERIFIED
                                    ? 'Make sure your personal details are correct. After completing KYC verification, you won’t be able to change them'
                                    : 'Once KYC verification is completed, your personal details cannot be edited'}
                            </p>
                        </div>
                    </div>
                    {user?.status === EUserStatus.UNVERIFIED && !isEdit && (
                        <Button
                            leftIcon={isEdit ? undefined : 'edit'}
                            variant="outlined-gold"
                            size="small"
                            onClick={onSubmit}>
                            {!isMobile && (isEdit ? submitLocale('save') : submitLocale('edit'))}
                        </Button>
                    )}
                </div>
                <div className={styles.content}>
                    {isEdit ? (
                        <>
                            <div className={styles.row}>
                                <div className={styles.data_wrapper}>
                                    <FormField
                                        name="profile.first_name"
                                        renderComponent={(props) => (
                                            <Input
                                                label={labelLocale('first_name')}
                                                {...props}
                                                full
                                                readOnly={!isEdit}
                                            />
                                        )}
                                    />
                                </div>
                                <div className={styles.data_wrapper}>
                                    <FormField
                                        name="profile.last_name"
                                        renderComponent={(props) => (
                                            <Input
                                                label={labelLocale('last_name')}
                                                {...props}
                                                full
                                                readOnly={!isEdit}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                            <div className={styles.row}>
                                <div className={styles.data_wrapper}>
                                    <FormField<Date | null>
                                        name="profile.birth_date"
                                        renderComponent={(props) => (
                                            <DatePicker
                                                {...props}
                                                label={labelLocale('birth_date')}
                                                isBirthday
                                                full
                                                // readOnly
                                                readOnly={!isEdit}
                                                maxDate={getBirthDisabledDates()}
                                            />
                                        )}
                                    />
                                </div>
                                <div className={styles.data_wrapper}>
                                    <FormField<string | number>
                                        name="profile.occupancy"
                                        renderComponent={(props) => (
                                            <Select
                                                {...props}
                                                full
                                                isAllowSearch
                                                readOnly={!isEdit}
                                                label={labelLocale('occupation')}
                                                items={mapOccupationsToOptions()}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className={styles.row}>
                                <div className={styles.data_wrapper}>
                                    <p className={styles.label}>First name</p>
                                    <p className={styles.value}>{user?.profile?.first_name}</p>
                                </div>
                                <div className={styles.data_wrapper}>
                                    <p className={styles.label}>Last name</p>
                                    <p className={styles.value}>{user?.profile?.last_name}</p>
                                </div>
                            </div>
                            <div className={styles.row}>
                                <div className={styles.data_wrapper}>
                                    <p className={styles.label}>Date of birth</p>
                                    <p className={styles.value}>
                                        {formatDate(user?.profile?.birth_date, 'MM/DD/YYYY')}
                                    </p>
                                </div>
                                <div className={styles.data_wrapper}>
                                    <p className={styles.label}>Occupation</p>
                                    <p className={styles.value}>
                                        {/*//@ts-ignore*/}
                                        {occupation?.[user?.profile?.occupancy]}
                                    </p>
                                </div>
                            </div>
                        </>
                    )}
                    {isEdit && (
                        <div className={styles.actions}>
                            <Button
                                onClick={() => {
                                    setIsEdit(false);
                                    form.reset(user);
                                }}
                                variant="outlined-primary"
                                size="medium">
                                {submitLocale('cancel')}
                            </Button>
                            <Button
                                isLoading={isLoading}
                                onClick={onSubmit}
                                variant="primary"
                                size="medium">
                                {submitLocale('save_changes')}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default PersonalSection;
